import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { routes } from '@/utils/routes';
import { queryClient } from '@/queryClient';
import useFirebase from '@/hooks/useFirebase';
import { getAccounts } from '@/services/account';
import { auth } from '@/services/firebase';
import { getCustomer } from '@/services/customer';
import { retrievePicture } from '@/services/displayPicture';
import { TCustomer } from '../types/Customer';
import { TDecodedTokenPayload } from '../types/Payload';
import { onAuthStateChanged } from 'firebase/auth';
import { TBusiness } from '../types/Business';
import {
  APP_IS_OPEN_LOCAL_STORAGE,
  INTERCOM_USER_JWT,
  MAIN_TAB_SESSION_STORAGE_NAME,
  REFRESHED_SESSION_STORAGE_NAME,
  WEB_SESSION
} from '@/utils/browserStorageNames';
import { GetWebSessionResponse } from '@/services/webSession';
import { AccountType, AccountTypeEnum } from '../types/Account';
import { TRsPasskeyAuthenticationLoginResponse } from '@/services/types/Passkey';
import { tokenStore } from '@/services/tokenStore';
import { accountEmailStore } from '@/services/accountEmailStore';
import { isErrorType } from '@/services/types/ResponseBody';
import { BusinessAccessLevelEnum } from '@/services/types/Account';

// TODO: match on code alone once the API returns a signature-specific one. 5001 is
// currently returned for unrelated failures too, so the message has to disambiguate —
// which means a backend copy change silently disables this guard.
const DEVICE_NOT_AUTHORISED_CODE = '5001';
const DEVICE_NOT_AUTHORISED_MESSAGE = 'Device is not authorised';

const isDeviceNotAuthorisedError = (err: unknown): boolean =>
  isErrorType(err) &&
  err.code === DEVICE_NOT_AUTHORISED_CODE &&
  err.message === DEVICE_NOT_AUTHORISED_MESSAGE;

export const AuthContext = createContext<{
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  isLoggedIn: boolean;
  isForceLogout: boolean;
  setIsForceLogout: Dispatch<SetStateAction<boolean>>;
  onLogin: (data: GetWebSessionResponse) => void;
  onPasskeyLogin: (data: TRsPasskeyAuthenticationLoginResponse) => void;
  onLogout: (wasSessionCreated?: boolean) => void;
  beginPasskeyFlow: () => void;
  cancelPasskeyFlow: () => void;
  finalizePasskeyFlow: () => Promise<void>;
  accountId: string | undefined;
  accountEmail: string | undefined;
  accountEmailConfirmed: boolean;
  business: TBusiness | undefined;
  customer: TCustomer | undefined;
  accountType: AccountType | undefined;
  photoURL: string | undefined;
  businessPhotoURL: string | undefined;
} | null>(null);

const AuthProvider: React.FC<React.HTMLProps<HTMLDivElement>> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isForceLogout, setIsForceLogout] = useState(false);
  const accountId = useRef<string | undefined>(undefined);
  const accountEmail = useRef<string | undefined>(undefined);
  const accountEmailConfirmed = useRef<boolean>(false);
  const business = useRef<TBusiness | undefined>(undefined);
  const customer = useRef<TCustomer | undefined>(undefined);
  const accountType = useRef<AccountType | undefined>(undefined);
  const photoURL = useRef<string | undefined>(undefined);
  const businessPhotoURL = useRef<string | undefined>(undefined);
  const isPasskeyFlowActiveRef = useRef(false);
  const isLoginInProgressRef = useRef(false);
  const { login, logout } = useFirebase();

  const getLoggedInUserDetails = async (token: string) => {
    const decoded: TDecodedTokenPayload & { claims: Record<string, string> } = jwtDecode(token);
    const loggedInAccountId = decoded.accountId ?? decoded.claims?.accountId;

    const response = await getAccounts();
    const accounts = response.data ?? [];
    const foundAccount = accounts.find((account) => account.id === loggedInAccountId);

    if (foundAccount?.business == null) {
      accountType.current = AccountTypeEnum.PERSONAL;
    } else {
      accountType.current = AccountTypeEnum.BUSINESS;
      business.current = {
        id: foundAccount?.business.businessId,
        name: foundAccount?.business.businessName ?? '',
        accessLevel: foundAccount?.business.accessLevel ?? BusinessAccessLevelEnum.OWNER // Type-safe fallback; accessLevel is not expected to be null.
      };
      const businessPicResponse = await retrievePicture(business.current.id, 'business');
      businessPhotoURL.current = businessPicResponse.data?.url ?? undefined;
    }

    accountEmail.current = foundAccount?.email ?? undefined;
    accountEmailConfirmed.current = foundAccount?.emailConfirmed ?? false;
    accountEmailStore.set({
      email: accountEmail.current,
      confirmed: accountEmailConfirmed.current
    });

    const customerRes = await getCustomer();
    customer.current = {
      id: customerRes.data.id,
      name: [customerRes.data.firstName, customerRes.data.lastName].join(' ') ?? '',
      email: customerRes?.data?.email,
      emailConfirmed: customerRes.data.emailConfirmed,
      preferredName: customerRes.data.preferredName
    };

    const pictureResponse = await retrievePicture(customerRes.data.id, 'customer');
    photoURL.current = pictureResponse.data?.url ?? undefined;

    // Left in session storage (signOut clears it) rather than consumed here: a page refresh
    // re-runs this via onAuthStateChanged, and reading a removed key booted Intercom with null.
    const intercomToken = sessionStorage.getItem(INTERCOM_USER_JWT);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    (window as any).Intercom('boot', {
      app_id: process.env.REACT_APP_INTERCOM_APP_ID,
      intercom_user_jwt: intercomToken,
      name:
        customerRes.data.preferredName ??
        [customerRes.data.firstName, customerRes.data.lastName].join(' '),
      email: customerRes?.data?.email,
      user_id: `emerge-${customerRes.data.id}`,
      hide_default_launcher: true,
      customAttributes: {
        /* prettier-ignore */
        'Product': 'emerge'
      }
    });

    accountId.current = loggedInAccountId;
  };

  const handleLogin = useCallback(async (webSession: GetWebSessionResponse) => {
    // Every login call rotates the refresh cookie and the CSRF token paired with it, so the
    // newest response has to win or refresh fails as an invalid token. Applied ahead of the
    // duplicate guard below so a redundant call can never leave the stored pair behind.
    if (webSession.accessToken) {
      tokenStore.setAccessToken(webSession.accessToken);
    }
    if (webSession.csrfToken) {
      tokenStore.setCsrfToken(webSession.csrfToken);
    }

    if (isLoginInProgressRef.current) return;
    isLoginInProgressRef.current = true;
    try {
      sessionStorage.setItem(WEB_SESSION, webSession.id!);
      sessionStorage.setItem(INTERCOM_USER_JWT, webSession.intercomUserToken!);

      await login(webSession.token!);

      // onAuthStateChanged does not fire on same-UID re-signin (passkey flow's
      // second token has new claims but identical UID), so fetch details here.
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken();
        await getLoggedInUserDetails(idToken);
      }

      setIsLoggedIn(true);
      navigate(routes.home);
    } finally {
      isLoginInProgressRef.current = false;
    }
  }, []);

  const handlePasskeyLogin = useCallback(
    async (passkeyResponse: TRsPasskeyAuthenticationLoginResponse) => {
      if (passkeyResponse.accessToken) {
        tokenStore.setAccessToken(passkeyResponse.accessToken);
      }
      if (passkeyResponse.csrfToken) {
        tokenStore.setCsrfToken(passkeyResponse.csrfToken);
      }
      await login(passkeyResponse.signInToken!);
    },
    []
  );

  const beginPasskeyFlow = useCallback(() => {
    isPasskeyFlowActiveRef.current = true;
  }, []);

  const cancelPasskeyFlow = useCallback(() => {
    isPasskeyFlowActiveRef.current = false;
  }, []);

  const finalizePasskeyFlow = useCallback(async () => {
    // The webSession useEffect will fire handleLogin with the new tokens;
    // handleLogin does the Firebase signin + getLoggedInUserDetails + navigate.
    isPasskeyFlowActiveRef.current = false;
  }, []);

  const redirectToBaseRoute = () => {
    accountId.current = undefined;
    accountEmail.current = undefined;
    accountEmailConfirmed.current = false;
    accountEmailStore.set({ email: undefined, confirmed: false });
    business.current = undefined;
    customer.current = undefined;
    photoURL.current = undefined;
    businessPhotoURL.current = undefined;
    // React Query's cache is a singleton with staleTime: Infinity (see queryClient.ts) and
    // otherwise outlives this session entirely - without this, a second customer who logs into
    // the same business in this tab (no full page reload in between) could silently reuse the
    // first customer's cached team members/cards, since neither is scoped by customer id.
    queryClient.clear();
    navigate(routes.base);
  };

  const abandonTabSession = () => {
    sessionStorage.removeItem(WEB_SESSION);
    setIsLoggedIn(false);
    redirectToBaseRoute();
  };

  const handleLogout = useCallback(async (wasSessionCreated: boolean = true) => {
    try {
      await logout(wasSessionCreated);
    } catch (err: any) {
      if (isErrorType(err)) {
        if (err.code !== '401') {
          throw err;
        }
      } else {
        throw err;
      }
    }

    tokenStore.clear();

    /* eslint-disable @typescript-eslint/no-explicit-any */
    (window as any).Intercom('shutdown');

    setIsForceLogout(true);
    setIsLoggedIn(false);
    redirectToBaseRoute();
  }, []);

  useEffect(() => {
    const handleTabCloseLogout = async () => {
      await logout();
      setIsLoggedIn(false);
      navigate(routes.base);
    };

    const unregisterAuthObserver = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        const url = new URL(window.location.href);
        navigate({ pathname: routes.base, search: url.search });
        return;
      }

      // Check if the application is already open
      if (localStorage.getItem(APP_IS_OPEN_LOCAL_STORAGE)) {
        sessionStorage.setItem(REFRESHED_SESSION_STORAGE_NAME, 'true');
      } else {
        localStorage.setItem(APP_IS_OPEN_LOCAL_STORAGE, 'true');
        sessionStorage.setItem(MAIN_TAB_SESSION_STORAGE_NAME, 'true');

        window.addEventListener('beforeunload', function () {
          localStorage.removeItem(APP_IS_OPEN_LOCAL_STORAGE);
        });
      }

      if (
        !sessionStorage.getItem(REFRESHED_SESSION_STORAGE_NAME) ||
        sessionStorage.getItem(REFRESHED_SESSION_STORAGE_NAME) == 'false'
      ) {
        handleTabCloseLogout();
        return;
      }

      sessionStorage.setItem(REFRESHED_SESSION_STORAGE_NAME, 'true');
      setIsLoggedIn(true);
      setIsForceLogout(false);

      if (isLoginInProgressRef.current) {
        // handleLogin is driving the user-details fetch
        return;
      }

      if (isPasskeyFlowActiveRef.current) {
        // Login.tsx will drive the rest after acknowledgeSession
        return;
      }

      if (!sessionStorage.getItem(WEB_SESSION)) {
        abandonTabSession();
        return;
      }

      try {
        await getLoggedInUserDetails(await user!.getIdToken());
      } catch (err: unknown) {
        // A revoked or stale signature surfaces here; no state in this tab can recover it.
        if (isDeviceNotAuthorisedError(err)) {
          abandonTabSession();
          return;
        }
        // Transient failures (network, 5xx) keep today's behaviour rather than bouncing
        // the user out of a healthy session.
        throw err;
      }

      const url = new URL(window.location.href);

      const isKnownRoute =
        location.pathname !== routes.base &&
        Object.values(routes).some((pattern) => matchPath(pattern, location.pathname));
      navigate({
        pathname: isKnownRoute ? location.pathname : routes.home,
        search: url.search
      });
    });
    return () => {
      unregisterAuthObserver();
      if (sessionStorage.getItem(MAIN_TAB_SESSION_STORAGE_NAME) == 'true') {
        window.removeEventListener('beforeunload', function () {
          localStorage.removeItem(APP_IS_OPEN_LOCAL_STORAGE);
        });
      }
    };
  }, []);

  //Refresh token logout on failed refresh
  useEffect(() => {
    const onRefreshFailed = () => {
      if (isLoggedIn) {
        handleLogout();
      } else {
        redirectToBaseRoute();
      }
    };
    window.addEventListener('auth:refresh-failed', onRefreshFailed);
    return () => window.removeEventListener('auth:refresh-failed', onRefreshFailed);
  }, []);

  const value = {
    isLoggedIn,
    isForceLogout,
    setIsForceLogout,
    onLogin: handleLogin,
    onPasskeyLogin: handlePasskeyLogin,
    onLogout: handleLogout,
    beginPasskeyFlow,
    cancelPasskeyFlow,
    finalizePasskeyFlow,
    accountId: accountId.current,
    accountEmail: accountEmail.current,
    accountEmailConfirmed: accountEmailConfirmed.current,
    setIsLoggedIn,
    business: business.current,
    customer: customer.current,
    accountType: accountType.current,
    photoURL: photoURL.current,
    businessPhotoURL: businessPhotoURL.current
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
